/**
 * Floating Chat Panel
 * Global chat interface with memory, birth charts, and topic management
 * Supports drag, resize, and debug info panel
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agentApi } from '@/lib/api';
import { chatApi, type ChatMessage, type ChatTopic, type ToolCallInfo, type StreamMetrics, type VoiceStreamCallbacks } from '@/lib/api/chat';
import { useSpaceToTalk } from '@/hooks/useVoiceRecording';
import { memoryApi, MemoryScope, CategoryType } from '@/lib/api/memory';
import { birthChartApi, type BirthChart } from '@/lib/api/birthChart';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  MessageSquare,
  X,
  Minimize2,
  Maximize2,
  Send,
  Loader2,
  Brain,
  Star,
  ChevronDown,
  ChevronRight,
  Trash2,
  Plus,
  Edit2,
  Search,
  Wrench,
  Clock,
  Zap,
  History,
  Filter,
  MapPin,
  User,
  Calendar,
  Check,
  Tag,
  Bug,
  Move,
  TrendingUp,
  Mic,
  ImageIcon,
} from 'lucide-react';
import { type UploadedImage } from '@/features/playground/components/ImageUpload';
import { PromoteChatDialog, usePromoteChat, useEvalCategories } from '@/features/eval-scenarios';
import { ThinkingCard, isThinkingTool } from '@/components/chat/ThinkingCard';

type PanelTab = 'chat' | 'topics' | 'memory' | 'birthcharts';

// Debug panel width
const DEBUG_PANEL_WIDTH = 280;

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  defaultAgentType?: string;
}

interface DisplayMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  toolCalls?: ToolCallInfo[];
  images?: string[]; // Image preview URLs for display
  metrics?: {
    total_tokens?: number;
    execution_time?: string;
  };
}

// Helper function to format relative time
const formatRelativeTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Metadata badge colors
const METADATA_COLORS: Record<string, string> = {
  agent_type: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
  source: 'bg-green-100 text-green-700 hover:bg-green-200',
  type: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
  date: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  session_id: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
};

const getMetadataBadgeColor = (field: string): string => {
  return METADATA_COLORS[field] || 'bg-slate-100 text-slate-600 hover:bg-slate-200';
};

// Minimum and default dimensions
const MIN_WIDTH = 400;
const MIN_HEIGHT = 400;
const DEFAULT_WIDTH = 520;
const DEFAULT_HEIGHT = 700;

// LocalStorage keys
const STORAGE_KEY_SIZE = 'chat_panel_size';

// Load saved size from localStorage
const getSavedSize = (): { width: number; height: number } => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_SIZE);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        width: Math.max(MIN_WIDTH, parsed.width || DEFAULT_WIDTH),
        height: Math.max(MIN_HEIGHT, parsed.height || DEFAULT_HEIGHT),
      };
    }
  } catch (e) {
    // Ignore errors
  }
  return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
};

export const ChatPanel: React.FC<ChatPanelProps> = ({
  isOpen,
  onClose,
  defaultAgentType,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState<PanelTab>('chat');
  const [selectedAgent, setSelectedAgent] = useState<string>(defaultAgentType || '');

  // Panel position and size state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState(getSavedSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0, posX: 0, posY: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Initialize position to bottom-right
  useEffect(() => {
    if (isOpen && position.x === 0 && position.y === 0) {
      setPosition({
        x: window.innerWidth - size.width - 16,
        y: window.innerHeight - size.height - 16,
      });
    }
  }, [isOpen, position.x, position.y, size.width, size.height]);

  // Chat state
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [topicId, setTopicId] = useState<string | null>(null);
  const [inputFocused, setInputFocused] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Streaming state
  const [streamingStartTime, setStreamingStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [streamingToolCalls, setStreamingToolCalls] = useState<ToolCallInfo[]>([]);
  const [streamingMetrics, setStreamingMetrics] = useState<StreamMetrics | null>(null);
  const [totalTokens, setTotalTokens] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioFileInputRef = useRef<HTMLInputElement>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  // Thinking card expansion state for mutual exclusion
  const [expandedThinkingCardId, setExpandedThinkingCardId] = useState<string | null>(null);
  const handleThinkingCardToggle = useCallback((toolId: string) => {
    setExpandedThinkingCardId(prevId => prevId === toolId ? null : toolId);
  }, []);

  // Image upload state
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  // Debug panel state
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // Debug state - Agent debugging info
  interface DebugEvent {
    time: string;
    type: 'init' | 'content' | 'tool_start' | 'tool_end' | 'tool_progress' | 'metrics' | 'done' | 'error' | 'request';
    data: any;
  }
  const [debugEvents, setDebugEvents] = useState<DebugEvent[]>([]);
  const [lastRequest, setLastRequest] = useState<any>(null);
  const [lastResponse, setLastResponse] = useState<any>(null);
  const [sessionMetrics, setSessionMetrics] = useState({
    totalRequests: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    avgResponseTime: 0,
    responseTimes: [] as number[],
  });

  const addDebugEvent = (type: DebugEvent['type'], data: any) => {
    const now = new Date();
    const time = now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }) +
      '.' + String(now.getMilliseconds()).padStart(3, '0');
    const event: DebugEvent = {
      time,
      type,
      data,
    };
    setDebugEvents(prev => [...prev.slice(-50), event]); // Keep last 50 events
  };

  const clearDebugEvents = () => {
    setDebugEvents([]);
    setLastRequest(null);
    setLastResponse(null);
  };

  // Topics state
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editingTopicName, setEditingTopicName] = useState('');

  // Promote chat state
  const [promoteTopicId, setPromoteTopicId] = useState<string | null>(null);
  const [promoteTopicTitle, setPromoteTopicTitle] = useState<string>('');
  const promoteMutation = usePromoteChat();
  const { data: evalCategories = [] } = useEvalCategories();

  // Drag handlers
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, input, select')) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: position.x,
      posY: position.y,
    };
  }, [position]);

  const handleDragMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    const newX = Math.max(0, Math.min(window.innerWidth - size.width, dragStartRef.current.posX + deltaX));
    const newY = Math.max(0, Math.min(window.innerHeight - size.height, dragStartRef.current.posY + deltaY));
    setPosition({ x: newX, y: newY });
  }, [isDragging, size.width, size.height]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      posX: position.x,
      posY: position.y,
    };
  }, [size, position]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeDirection) return;

    const deltaX = e.clientX - resizeStartRef.current.x;
    const deltaY = e.clientY - resizeStartRef.current.y;

    let newWidth = resizeStartRef.current.width;
    let newHeight = resizeStartRef.current.height;
    let newX = resizeStartRef.current.posX;
    let newY = resizeStartRef.current.posY;

    if (resizeDirection.includes('e')) {
      newWidth = Math.max(MIN_WIDTH, resizeStartRef.current.width + deltaX);
    }
    if (resizeDirection.includes('w')) {
      const widthDelta = Math.min(deltaX, resizeStartRef.current.width - MIN_WIDTH);
      newWidth = resizeStartRef.current.width - widthDelta;
      newX = resizeStartRef.current.posX + widthDelta;
    }
    if (resizeDirection.includes('s')) {
      newHeight = Math.max(MIN_HEIGHT, resizeStartRef.current.height + deltaY);
    }
    if (resizeDirection.includes('n')) {
      const heightDelta = Math.min(deltaY, resizeStartRef.current.height - MIN_HEIGHT);
      newHeight = resizeStartRef.current.height - heightDelta;
      newY = resizeStartRef.current.posY + heightDelta;
    }

    setSize({ width: newWidth, height: newHeight });
    setPosition({ x: Math.max(0, newX), y: Math.max(0, newY) });
  }, [isResizing, resizeDirection]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeDirection(null);
    // Save size to localStorage
    localStorage.setItem(STORAGE_KEY_SIZE, JSON.stringify(size));
  }, [size]);

  // Global mouse event listeners for drag and resize
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Memory state
  const [memorySearch, setMemorySearch] = useState('');
  const [expandedMemories, setExpandedMemories] = useState<Set<string>>(new Set());
  const [activeMemoryFilters, setActiveMemoryFilters] = useState<Record<string, string>>({});
  const [memoryScope, setMemoryScope] = useState<MemoryScope>('all');
  const [categoryType, setCategoryType] = useState<CategoryType>('all');

  // Birth chart state
  const [showBirthChartForm, setShowBirthChartForm] = useState(false);
  const [editingChartId, setEditingChartId] = useState<string | null>(null);
  const [chartForm, setChartForm] = useState({
    name: '',
    birth_date: '',
    birth_time: '',
    birth_place: '',
    gender: '',
    relation: '',
    notes: '',
  });

  // Fetch agents list
  const { data: agentsResponse } = useQuery({
    queryKey: ['agents-list', false],
    queryFn: () => agentApi.list(false),
  });
  const agentsList = agentsResponse?.agents || [];

  // Fetch topics for selected agent
  const { data: topics, refetch: refetchTopics } = useQuery({
    queryKey: ['chat-topics', selectedAgent],
    queryFn: () => chatApi.getTopics(selectedAgent),
    enabled: !!selectedAgent,
  });

  // Sort topics by updated_at descending
  const sortedTopics = useMemo(() => {
    if (!topics) return [];
    return [...topics].sort((a, b) => {
      const timeA = new Date(a.updated_at || a.created_at).getTime();
      const timeB = new Date(b.updated_at || b.created_at).getTime();
      return timeB - timeA;
    });
  }, [topics]);

  // Fetch memories for selected agent with filters
  const { data: memories, refetch: refetchMemories, isLoading: loadingMemories } = useQuery({
    queryKey: ['agent-memories', selectedAgent, memoryScope, categoryType],
    queryFn: () => memoryApi.list(selectedAgent, { memory_scope: memoryScope, category_type: categoryType }),
    enabled: !!selectedAgent && activeTab === 'memory',
  });

  // Sort memories by last_updated descending
  const sortedMemories = useMemo(() => {
    if (!memories) return [];
    return [...memories].sort((a, b) => {
      const timeA = new Date(a.last_updated || a.updated_at || a.created_at || 0).getTime();
      const timeB = new Date(b.last_updated || b.updated_at || b.created_at || 0).getTime();
      return timeB - timeA;
    });
  }, [memories]);

  // Filter memories by search and metadata filters
  const filteredMemories = useMemo(() => {
    let result = sortedMemories;

    // Apply search filter
    if (memorySearch) {
      result = result.filter((m) =>
        m.memory.toLowerCase().includes(memorySearch.toLowerCase())
      );
    }

    // Apply metadata filters (AND logic)
    if (Object.keys(activeMemoryFilters).length > 0) {
      result = result.filter((memory) => {
        if (!memory.metadata) return false;
        return Object.entries(activeMemoryFilters).every(
          ([field, value]) => memory.metadata?.[field] === value
        );
      });
    }

    return result;
  }, [sortedMemories, memorySearch, activeMemoryFilters]);

  // Fetch birth charts
  const { data: birthChartsData, refetch: refetchBirthCharts, isLoading: loadingBirthCharts } = useQuery({
    queryKey: ['birth-charts'],
    queryFn: () => birthChartApi.list(),
    enabled: activeTab === 'birthcharts',
  });

  // Sort birth charts: self first, then by created_at
  const sortedBirthCharts = useMemo(() => {
    const charts = birthChartsData || [];
    return [...charts].sort((a, b) => {
      if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
      if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
      const timeA = new Date(a.created_at).getTime();
      const timeB = new Date(b.created_at).getTime();
      return timeB - timeA;
    });
  }, [birthChartsData]);

  // Set default agent when agents load (only once when no agent selected)
  useEffect(() => {
    if (agentsList.length && !selectedAgent) {
      setSelectedAgent(defaultAgentType || agentsList[0].agent_type);
    }
  }, [agentsList, defaultAgentType, selectedAgent]);

  // Sync with URL when navigating to different agent pages
  useEffect(() => {
    if (defaultAgentType && agentsList.length > 0) {
      // Check if the agent exists in the list
      const agentExists = agentsList.some(a => a.agent_type === defaultAgentType);
      if (agentExists) {
        setSelectedAgent(defaultAgentType);
      }
    }
  }, [defaultAgentType, agentsList]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingToolCalls]);

  // Check if selected agent has multimodal enabled
  const selectedAgentConfig = useMemo(() => {
    return agentsList.find(a => a.agent_type === selectedAgent)?.configuration;
  }, [agentsList, selectedAgent]);

  const isMultimodalEnabled = selectedAgentConfig?.multimodal_enabled ?? false;

  // Clear chat when agent changes
  useEffect(() => {
    setMessages([]);
    setTopicId(null);
    setTotalTokens(0);
    setActiveMemoryFilters({});
    setUploadedImages([]); // Clear images when agent changes
  }, [selectedAgent]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const handleSend = async () => {
    const hasText = inputValue.trim().length > 0;
    const hasImages = uploadedImages.length > 0;
    if ((!hasText && !hasImages) || isStreaming || !selectedAgent) return;

    // Prepare images for display and API
    const imageUrls = uploadedImages.map(img => img.preview);
    const imagesForApi = uploadedImages.map(img => ({
      url: img.base64, // Send base64 data URL to backend
      detail: 'auto' as const,
    }));

    const userMessage: DisplayMessage = {
      role: 'user',
      content: inputValue.trim() || (hasImages ? '[Image]' : ''),
      timestamp: new Date(),
      images: imageUrls, // Store preview URLs for display
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setUploadedImages([]); // Clear images after sending
    setIsStreaming(true);

    // Start timer
    const startTime = Date.now();
    setStreamingStartTime(startTime);
    setElapsedTime(0);
    setStreamingToolCalls([]);
    setStreamingMetrics(null);

    timerRef.current = setInterval(() => {
      setElapsedTime((Date.now() - startTime) / 1000);
    }, 100);

    // Add placeholder for assistant message
    const assistantMessage: DisplayMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      toolCalls: [],
    };
    setMessages((prev) => [...prev, assistantMessage]);

    const chatMessages: ChatMessage[] = [{ role: 'user', content: userMessage.content }];

    // Debug: Log request
    const requestPayload = {
      model: selectedAgent,
      messages: chatMessages,
      stream: true,
      topic_id: topicId || undefined,
      images: imagesForApi.length > 0 ? imagesForApi : undefined,
    };
    setLastRequest(requestPayload);
    addDebugEvent('request', { agent: selectedAgent, topic_id: topicId, message_length: userMessage.content.length, image_count: imagesForApi.length });

    try {
      abortControllerRef.current = chatApi.sendStreaming(
        selectedAgent,
        {
          messages: chatMessages,
          stream: true,
          topic_id: topicId || undefined,
          images: imagesForApi.length > 0 ? imagesForApi : undefined,
        },
        {
          onInit: (event) => {
            addDebugEvent('init', event);
            if (event.topic_id && !topicId) {
              setTopicId(event.topic_id);
            }
          },
          onContent: (content) => {
            // Don't log every content chunk to avoid spam, just update message
            // IMPORTANT: Create new object to avoid mutation issues with React StrictMode
            setMessages((prev) => {
              const lastIdx = prev.length - 1;
              const lastMsg = prev[lastIdx];
              if (lastMsg?.role === 'assistant') {
                return [
                  ...prev.slice(0, lastIdx),
                  { ...lastMsg, content: lastMsg.content + content },
                ];
              }
              return prev;
            });
          },
          onToolCallStart: (tool) => {
            addDebugEvent('tool_start', { name: tool.name, id: tool.id });
            const newToolCall: ToolCallInfo = {
              name: tool.name,
              id: tool.id,
              status: 'running',
              startTime: Date.now(),
              input: tool.input, // Include tool input for thinking tools
            };
            setStreamingToolCalls((prev) => [...prev, newToolCall]);
          },
          onToolInputUpdate: (toolId, input) => {
            // Update tool input as it streams (for think/analyze tools)
            setStreamingToolCalls((prev) => {
              const updated = [...prev];
              const tool = updated.find((t) => t.id === toolId);
              if (tool) {
                tool.input = input;
              }
              return updated;
            });
          },
          onToolCallEnd: (metadata) => {
            addDebugEvent('tool_end', metadata);
            setStreamingToolCalls((prev) => {
              const updated = [...prev];
              const runningTool = updated.find((t) => t.status === 'running');
              if (runningTool) {
                runningTool.status = 'completed';
                runningTool.executionTime = metadata.executionTime;
                if (metadata.toolName) {
                  runningTool.name = metadata.toolName;
                }
                runningTool.images = metadata.images;
              }
              return updated;
            });
          },
          onToolProgress: (toolId, progress) => {
            addDebugEvent('tool_progress', { toolId, progress });
            setStreamingToolCalls((prev) => {
              const updated = [...prev];
              const tool = updated.find((t) => t.id === toolId);
              if (tool) {
                tool.progress = progress;
              }
              return updated;
            });
          },
          onMetrics: (metrics) => {
            addDebugEvent('metrics', metrics);
            setStreamingMetrics(metrics);
          },
          onDone: () => {
            // Clear timer
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }

            const finalElapsed = streamingStartTime ? (Date.now() - streamingStartTime) / 1000 : 0;

            // Debug: Log completion
            addDebugEvent('done', {
              elapsed: finalElapsed.toFixed(2),
              tool_calls: streamingToolCalls.length,
              input_tokens: streamingMetrics?.input_tokens,
              output_tokens: streamingMetrics?.output_tokens,
            });

            // Update session metrics
            setSessionMetrics(prev => {
              const newResponseTimes = [...prev.responseTimes, finalElapsed];
              const avgTime = newResponseTimes.reduce((a, b) => a + b, 0) / newResponseTimes.length;
              return {
                totalRequests: prev.totalRequests + 1,
                totalInputTokens: prev.totalInputTokens + (streamingMetrics?.input_tokens || 0),
                totalOutputTokens: prev.totalOutputTokens + (streamingMetrics?.output_tokens || 0),
                avgResponseTime: avgTime,
                responseTimes: newResponseTimes.slice(-20), // Keep last 20
              };
            });

            // Update the last message with final data
            // IMPORTANT: Create new object to avoid mutation issues with React StrictMode
            setMessages((prev) => {
              const lastIdx = prev.length - 1;
              const lastMsg = prev[lastIdx];
              if (lastMsg?.role === 'assistant') {
                const updatedMsg = {
                  ...lastMsg,
                  isStreaming: false,
                  toolCalls: [...streamingToolCalls],
                  metrics: {
                    total_tokens: (streamingMetrics?.input_tokens || 0) + (streamingMetrics?.output_tokens || 0),
                    execution_time: finalElapsed.toFixed(2),
                  },
                };
                setLastResponse({
                  content_length: updatedMsg.content.length,
                  tool_calls: streamingToolCalls.length,
                  metrics: updatedMsg.metrics,
                });
                return [...prev.slice(0, lastIdx), updatedMsg];
              }
              return prev;
            });

            // Update total tokens
            if (streamingMetrics) {
              const tokens = (streamingMetrics.input_tokens || 0) + (streamingMetrics.output_tokens || 0);
              setTotalTokens((prev) => prev + tokens);
            }

            setIsStreaming(false);
            setStreamingStartTime(null);
            setStreamingToolCalls([]);
            setStreamingMetrics(null);
            refetchTopics();
          },
          onError: (error) => {
            addDebugEvent('error', { message: error });

            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }

            // IMPORTANT: Create new object to avoid mutation issues with React StrictMode
            setMessages((prev) => {
              const lastIdx = prev.length - 1;
              const lastMsg = prev[lastIdx];
              if (lastMsg?.role === 'assistant') {
                return [
                  ...prev.slice(0, lastIdx),
                  { ...lastMsg, content: `Error: ${error}`, isStreaming: false },
                ];
              }
              return prev;
            });

            setIsStreaming(false);
            setStreamingStartTime(null);
          },
        }
      );
    } catch (error) {
      console.error('Failed to send:', error);
      addDebugEvent('error', { message: String(error) });
      setIsStreaming(false);
    }
  };

  // Voice recording handler
  const handleVoiceSend = useCallback(async (audioBlob: Blob) => {
    if (isStreaming || !selectedAgent) return;

    setIsStreaming(true);

    // Start timer
    const startTime = Date.now();
    setStreamingStartTime(startTime);
    setElapsedTime(0);
    setStreamingToolCalls([]);
    setStreamingMetrics(null);

    timerRef.current = setInterval(() => {
      setElapsedTime((Date.now() - startTime) / 1000);
    }, 100);

    // Debug: Log voice request
    const requestPayload = {
      model: selectedAgent,
      type: 'voice',
      audio_size: audioBlob.size,
      topic_id: topicId || undefined,
    };
    setLastRequest(requestPayload);
    addDebugEvent('request', { agent: selectedAgent, topic_id: topicId, type: 'voice', audio_size: audioBlob.size });

    const callbacks: VoiceStreamCallbacks = {
      onTranscript: (text) => {
        // Add user message with transcribed text
        const userMessage: DisplayMessage = {
          role: 'user',
          content: text,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);
        addDebugEvent('init', { transcript: text.slice(0, 50) + '...' });
      },
      onInit: (event) => {
        if (event.topic_id && !topicId) {
          setTopicId(event.topic_id);
        }
        // Add placeholder for assistant message if not already added
        setMessages((prev) => {
          const lastMsg = prev[prev.length - 1];
          if (!lastMsg || lastMsg.role !== 'assistant') {
            return [...prev, {
              role: 'assistant' as const,
              content: '',
              timestamp: new Date(),
              isStreaming: true,
              toolCalls: [],
            }];
          }
          return prev;
        });
      },
      onContent: (content) => {
        setMessages((prev) => {
          const lastIdx = prev.length - 1;
          const lastMsg = prev[lastIdx];
          if (lastMsg?.role === 'assistant') {
            return [
              ...prev.slice(0, lastIdx),
              { ...lastMsg, content: lastMsg.content + content },
            ];
          }
          return prev;
        });
      },
      onToolCallStart: (tool) => {
        addDebugEvent('tool_start', { name: tool.name, id: tool.id });
        const newToolCall: ToolCallInfo = {
          name: tool.name,
          id: tool.id,
          status: 'running',
          startTime: Date.now(),
          input: tool.input, // Include tool input for thinking tools
        };
        setStreamingToolCalls((prev) => [...prev, newToolCall]);
      },
      onToolInputUpdate: (toolId, input) => {
        // Update tool input as it streams (for think/analyze tools)
        setStreamingToolCalls((prev) => {
          const updated = [...prev];
          const tool = updated.find((t) => t.id === toolId);
          if (tool) {
            tool.input = input;
          }
          return updated;
        });
      },
      onToolCallEnd: (metadata) => {
        addDebugEvent('tool_end', metadata);
        setStreamingToolCalls((prev) => {
          const updated = [...prev];
          const runningTool = updated.find((t) => t.status === 'running');
          if (runningTool) {
            runningTool.status = 'completed';
            runningTool.executionTime = metadata.executionTime;
            if (metadata.toolName) {
              runningTool.name = metadata.toolName;
            }
            runningTool.images = metadata.images;
          }
          return updated;
        });
      },
      onToolProgress: (toolId, progress) => {
        addDebugEvent('tool_progress', { toolId, progress });
        setStreamingToolCalls((prev) => {
          const updated = [...prev];
          const tool = updated.find((t) => t.id === toolId);
          if (tool) {
            tool.progress = progress;
          }
          return updated;
        });
      },
      onMetrics: (metrics) => {
        addDebugEvent('metrics', metrics);
        setStreamingMetrics(metrics);
      },
      onDone: () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        const finalElapsed = streamingStartTime ? (Date.now() - streamingStartTime) / 1000 : 0;
        addDebugEvent('done', {
          elapsed: finalElapsed.toFixed(2),
          tool_calls: streamingToolCalls.length,
        });

        setSessionMetrics(prev => {
          const newResponseTimes = [...prev.responseTimes, finalElapsed];
          const avgTime = newResponseTimes.reduce((a, b) => a + b, 0) / newResponseTimes.length;
          return {
            totalRequests: prev.totalRequests + 1,
            totalInputTokens: prev.totalInputTokens + (streamingMetrics?.input_tokens || 0),
            totalOutputTokens: prev.totalOutputTokens + (streamingMetrics?.output_tokens || 0),
            avgResponseTime: avgTime,
            responseTimes: newResponseTimes.slice(-20),
          };
        });

        setMessages((prev) => {
          const lastIdx = prev.length - 1;
          const lastMsg = prev[lastIdx];
          if (lastMsg?.role === 'assistant') {
            const updatedMsg = {
              ...lastMsg,
              isStreaming: false,
              toolCalls: [...streamingToolCalls],
              metrics: {
                total_tokens: (streamingMetrics?.input_tokens || 0) + (streamingMetrics?.output_tokens || 0),
                execution_time: finalElapsed.toFixed(2),
              },
            };
            setLastResponse({
              content_length: updatedMsg.content.length,
              tool_calls: streamingToolCalls.length,
              metrics: updatedMsg.metrics,
            });
            return [...prev.slice(0, lastIdx), updatedMsg];
          }
          return prev;
        });

        if (streamingMetrics) {
          const tokens = (streamingMetrics.input_tokens || 0) + (streamingMetrics.output_tokens || 0);
          setTotalTokens((prev) => prev + tokens);
        }

        setIsStreaming(false);
        setStreamingStartTime(null);
        setStreamingToolCalls([]);
        setStreamingMetrics(null);
        refetchTopics();
      },
      onError: (error) => {
        addDebugEvent('error', { message: error });

        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        setMessages((prev) => {
          const lastIdx = prev.length - 1;
          const lastMsg = prev[lastIdx];
          if (lastMsg?.role === 'assistant') {
            return [
              ...prev.slice(0, lastIdx),
              { ...lastMsg, content: `Error: ${error}`, isStreaming: false },
            ];
          }
          return prev;
        });

        setIsStreaming(false);
        setStreamingStartTime(null);
      },
    };

    try {
      abortControllerRef.current = chatApi.sendVoiceStreaming(
        selectedAgent,
        audioBlob,
        topicId,
        callbacks
      );
    } catch (error) {
      console.error('Failed to send voice:', error);
      addDebugEvent('error', { message: String(error) });
      setIsStreaming(false);
    }
  }, [isStreaming, selectedAgent, topicId, streamingStartTime, streamingToolCalls, streamingMetrics, refetchTopics, addDebugEvent]);

  // Space-to-talk voice recording hook
  const voiceRecording = useSpaceToTalk({
    enabled: activeTab === 'chat' && !isStreaming,
    inputFocused,
    onRecordingComplete: handleVoiceSend,
    onError: (error) => {
      console.error('Voice recording error:', error);
      addDebugEvent('error', { type: 'voice_recording', message: error });
    },
  });

  // Handle audio file selection
  const handleAudioFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/webm', 'audio/ogg', 'audio/m4a', 'audio/mp4'];
    if (!validTypes.some(type => file.type.startsWith(type.split('/')[0]))) {
      addDebugEvent('error', { type: 'audio_file', message: `Invalid file type: ${file.type}` });
      return;
    }

    // Validate file size (max 25MB for GLM-ASR)
    if (file.size > 25 * 1024 * 1024) {
      addDebugEvent('error', { type: 'audio_file', message: 'File too large (max 25MB)' });
      return;
    }

    // Send the audio file
    handleVoiceSend(file);

    // Reset file input
    if (audioFileInputRef.current) {
      audioFileInputRef.current.value = '';
    }
  }, [handleVoiceSend, addDebugEvent]);

  // Handle image file selection
  const handleImageFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingImages(true);
    const newImages: UploadedImage[] = [];

    for (const file of Array.from(files)) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        addDebugEvent('error', { type: 'image_file', message: `Invalid file type: ${file.type}` });
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        addDebugEvent('error', { type: 'image_file', message: `File too large: ${file.name} (max 10MB)` });
        continue;
      }

      try {
        // Convert to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Create preview URL
        const preview = URL.createObjectURL(file);

        newImages.push({
          id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file,
          base64,
          preview,
          filename: file.name,
        });
      } catch (err) {
        addDebugEvent('error', { type: 'image_file', message: `Failed to process: ${file.name}` });
      }
    }

    setUploadedImages(prev => [...prev, ...newImages].slice(0, 4)); // Max 4 images
    setIsUploadingImages(false);

    // Reset file input
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = '';
    }
  }, [addDebugEvent]);

  // Handle image removal
  const handleRemoveImage = useCallback((imageId: string) => {
    setUploadedImages(prev => {
      const image = prev.find(img => img.id === imageId);
      if (image?.preview) {
        URL.revokeObjectURL(image.preview);
      }
      return prev.filter(img => img.id !== imageId);
    });
  }, []);

  const handleNewChat = () => {
    setMessages([]);
    setTopicId(null);
    setTotalTokens(0);
  };

  const handleLoadTopic = async (topic: ChatTopic) => {
    try {
      const topicMessages = await chatApi.getTopicMessages(selectedAgent, topic.topic_id);
      const displayMessages: DisplayMessage[] = topicMessages.map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
        timestamp: new Date(m.created_at),
        // Convert tool_calls with snake_case to camelCase
        toolCalls: m.tool_calls?.map((tool: any) => ({
          ...tool,
          // Map snake_case to camelCase for reasoning flag
          isReasoning: tool.isReasoning || tool.is_reasoning || false,
          // Ensure input is available (backend sends as both 'args' and 'input')
          input: tool.input || tool.args,
        })),
        metrics: m.metrics,
      }));
      setMessages(displayMessages);
      setTopicId(topic.topic_id);
      setActiveTab('chat');
    } catch (error) {
      console.error('Failed to load topic:', error);
    }
  };

  const handleDeleteTopic = async (topic: ChatTopic) => {
    if (!confirm(`Delete "${topic.title || topic.topic_id.slice(0, 8)}"? This cannot be undone.`)) {
      return;
    }
    try {
      await chatApi.deleteTopic(selectedAgent, topic.topic_id);
      refetchTopics();
      if (topicId === topic.topic_id) {
        handleNewChat();
      }
    } catch (error) {
      console.error('Failed to delete topic:', error);
    }
  };

  const handleRenameTopic = async (topic: ChatTopic, newName: string) => {
    try {
      await chatApi.renameTopic(selectedAgent, topic.topic_id, newName.trim());
      refetchTopics();
      setEditingTopicId(null);
      setEditingTopicName('');
    } catch (error) {
      console.error('Failed to rename topic:', error);
    }
  };

  const handleOpenPromoteDialog = (topic: ChatTopic) => {
    setPromoteTopicId(topic.topic_id);
    setPromoteTopicTitle(topic.title || `Chat ${topic.topic_id.slice(0, 8)}`);
  };

  const handlePromoteChat = async (data: import('@/types/evalScenarios').PromoteChatRequest) => {
    try {
      await promoteMutation.mutateAsync(data);
      setPromoteTopicId(null);
      setPromoteTopicTitle('');
      alert('Chat promoted to test scenario successfully!');
    } catch (error) {
      console.error('Failed to promote chat:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to promote chat: ${errorMessage}`);
    }
  };

  const handleDeleteMemory = async (memoryId: string) => {
    if (confirm('Delete this memory?')) {
      try {
        await memoryApi.delete(selectedAgent, memoryId);
        refetchMemories();
      } catch (error) {
        console.error('Failed to delete memory:', error);
      }
    }
  };

  const toggleMemoryFilter = (field: string, value: string) => {
    setActiveMemoryFilters((prev) => {
      const next = { ...prev };
      if (next[field] === value) {
        delete next[field];
      } else {
        next[field] = value;
      }
      return next;
    });
  };

  const clearMemoryFilters = () => {
    setActiveMemoryFilters({});
  };

  const handleSaveBirthChart = async () => {
    try {
      if (editingChartId) {
        await birthChartApi.update(editingChartId, chartForm);
      } else {
        await birthChartApi.create(chartForm);
      }
      refetchBirthCharts();
      setShowBirthChartForm(false);
      setEditingChartId(null);
      setChartForm({ name: '', birth_date: '', birth_time: '', birth_place: '', gender: '', relation: '', notes: '' });
    } catch (error) {
      console.error('Failed to save birth chart:', error);
    }
  };

  const handleEditBirthChart = (chart: BirthChart) => {
    setEditingChartId(chart.id);
    const chartData = chart.chart || chart;
    setChartForm({
      name: chart.nickname || chartData.name || '',
      birth_date: chartData.birth_date || '',
      birth_time: chartData.birth_time || '',
      birth_place: chartData.birth_location || chart.birth_place || '',
      gender: chartData.gender || '',
      relation: chart.relation || '',
      notes: chart.notes || '',
    });
    setShowBirthChartForm(true);
  };

  const handleDeleteBirthChart = async (chartId: string, chartName: string) => {
    if (confirm(`Delete birth chart "${chartName}"? This cannot be undone.`)) {
      try {
        await birthChartApi.delete(chartId);
        refetchBirthCharts();
      } catch (error) {
        console.error('Failed to delete birth chart:', error);
      }
    }
  };

  const handleSetPrimaryChart = async (chartId: string) => {
    try {
      await birthChartApi.setPrimary(chartId);
      refetchBirthCharts();
    } catch (error) {
      console.error('Failed to set primary chart:', error);
    }
  };

  // Get display name for birth chart
  const getChartDisplayName = (chart: BirthChart): string => {
    return chart.nickname || chart.chart?.name || chart.name || 'Unnamed';
  };

  // Get birth info for display
  const getChartBirthInfo = (chart: BirthChart) => {
    const chartData = chart.chart || chart;
    return {
      date: chartData.birth_date,
      time: chartData.birth_time,
      location: chartData.birth_location || chart.birth_place,
      timezone: chartData.timezone || chart.timezone,
      gender: chartData.gender || chart.gender,
      birthType: chartData.birth_type || chart.birth_type,
    };
  };

  // Panel width (debug is now internal tab)
  const totalWidth = size.width;

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className={`fixed z-50 flex ${isDragging ? 'cursor-grabbing' : ''}`}
      style={{
        left: isMinimized ? 'auto' : position.x,
        top: isMinimized ? 'auto' : position.y,
        right: isMinimized ? 16 : 'auto',
        bottom: isMinimized ? 16 : 'auto',
        width: isMinimized ? 320 : totalWidth,
        height: isMinimized ? 56 : size.height,
      }}
    >
      {/* Main Panel */}
      <div
        className={`bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col transition-colors ${
          isDragging ? 'border-blue-400' : ''
        }`}
        style={{
          width: isMinimized ? 320 : size.width,
          height: isMinimized ? 56 : size.height,
        }}
      >
        {/* Resize Handles */}
        {!isMinimized && (
          <>
            {/* Corners */}
            <div
              className="absolute -top-1 -left-1 w-3 h-3 cursor-nw-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 'nw')}
            />
            <div
              className="absolute -top-1 -right-1 w-3 h-3 cursor-ne-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 'ne')}
            />
            <div
              className="absolute -bottom-1 -left-1 w-3 h-3 cursor-sw-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 'sw')}
            />
            <div
              className="absolute -bottom-1 -right-1 w-3 h-3 cursor-se-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 'se')}
            />
            {/* Edges */}
            <div
              className="absolute -top-1 left-2 right-2 h-2 cursor-n-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 'n')}
            />
            <div
              className="absolute -bottom-1 left-2 right-2 h-2 cursor-s-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 's')}
            />
            <div
              className="absolute -left-1 top-2 bottom-2 w-2 cursor-w-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 'w')}
            />
            <div
              className="absolute -right-1 top-2 bottom-2 w-2 cursor-e-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 'e')}
            />
          </>
        )}

        {/* Header - Draggable, Double-click to toggle minimize */}
        <div
          className={`flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl ${
            !isMinimized ? 'cursor-grab' : ''
          } ${isDragging ? 'cursor-grabbing bg-blue-50' : ''}`}
          onMouseDown={handleDragStart}
          onDoubleClick={() => setIsMinimized(!isMinimized)}
        >
          <div className="flex items-center gap-2">
            <Move className="w-4 h-4 text-slate-400" />
            <MessageSquare className="w-5 h-5 text-slate-600" />
            <span className="font-medium text-slate-800">
              {selectedAgent
                ? agentsList.find(a => a.agent_type === selectedAgent)?.configuration.name || selectedAgent
                : 'Chat'}
            </span>
            {totalTokens > 0 && (
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                {totalTokens.toLocaleString()} tokens
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowDebugPanel(!showDebugPanel)}
              className={`p-1.5 rounded ${
                showDebugPanel
                  ? 'text-green-600 bg-green-100'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
              title="Toggle debug panel"
            >
              <Bug className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

      {!isMinimized && (
        <>
          {/* Agent Selector - Button Group */}
          <div className="px-4 py-2 border-b border-slate-100">
            <div className="flex flex-wrap gap-1.5">
              {agentsList.map((agent) => (
                <button
                  key={agent.agent_type}
                  onClick={() => setSelectedAgent(agent.agent_type)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    selectedAgent === agent.agent_type
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {agent.configuration.name}
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            {[
              { id: 'chat', icon: MessageSquare, label: 'Chat' },
              { id: 'topics', icon: History, label: 'History' },
              { id: 'memory', icon: Brain, label: 'Memory' },
              { id: 'birthcharts', icon: Star, label: 'Charts' },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id as PanelTab)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${
                  activeTab === id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Content - Horizontal split layout */}
          <div className="flex-1 overflow-hidden flex flex-row">
            {/* Main Content Area */}
            <div className={`flex-1 overflow-hidden flex flex-col ${showDebugPanel ? 'border-r border-slate-200' : ''}`}>
            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <>
                {/* Current Topic Info */}
                {topicId && (
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      Topic: {sortedTopics.find((t) => t.topic_id === topicId)?.title || topicId.slice(0, 8)}
                    </span>
                    <button
                      onClick={handleNewChat}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      New Chat
                    </button>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? (
                    <div className="text-center text-slate-400 py-8">
                      <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Start a conversation</p>
                    </div>
                  ) : (
                    messages.map((message, index) => (
                      <div key={index}>
                        <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                              message.role === 'user'
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-100 text-slate-800'
                            }`}
                          >
                            {/* Display images for user messages */}
                            {message.role === 'user' && message.images && message.images.length > 0 && (
                              <div className="mb-2 flex flex-wrap gap-1">
                                {message.images.map((imgUrl, imgIdx) => (
                                  <img
                                    key={imgIdx}
                                    src={imgUrl}
                                    alt={`Attached ${imgIdx + 1}`}
                                    className="w-20 h-20 object-cover rounded border border-blue-400"
                                  />
                                ))}
                              </div>
                            )}
                            <div className="whitespace-pre-wrap break-words">{message.content}</div>
                            {message.isStreaming && !message.content && (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            )}
                          </div>
                        </div>

                        {/* Tool calls for this message */}
                        {message.role === 'assistant' && message.toolCalls && message.toolCalls.length > 0 && (
                          <div className="mt-2 ml-2 space-y-1.5">
                            {/* Thinking tools get special ThinkingCard rendering with mutual exclusion */}
                            {message.toolCalls
                              .filter((tool) => isThinkingTool(tool.isReasoning))
                              .map((tool, tidx) => (
                                <ThinkingCard
                                  key={`thinking-${tidx}`}
                                  tool={tool}
                                  isStreaming={false}
                                  isExpanded={expandedThinkingCardId === tool.id}
                                  onToggle={handleThinkingCardToggle}
                                />
                              ))}
                            {/* Regular tools get standard badge rendering */}
                            {message.toolCalls
                              .filter((tool) => !isThinkingTool(tool.isReasoning))
                              .map((tool, tidx) => (
                                <div
                                  key={tidx}
                                  className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded"
                                >
                                  <Wrench className="w-3 h-3" />
                                  <span className="font-medium">{tool.name}</span>
                                  {tool.status === 'running' && (
                                    <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                                  )}
                                  {tool.executionTime && (
                                    <span className="text-emerald-600">
                                      {tool.executionTime.toFixed(2)}s
                                    </span>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}

                        {/* Metrics */}
                        {message.role === 'assistant' && message.metrics && !message.isStreaming && (
                          <div className="mt-1 ml-2 flex items-center gap-3 text-xs text-slate-400">
                            {message.metrics.execution_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {message.metrics.execution_time}s
                              </span>
                            )}
                            {message.metrics.total_tokens && message.metrics.total_tokens > 0 && (
                              <span className="flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                {message.metrics.total_tokens} tokens
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}

                  {/* Streaming tool calls */}
                  {isStreaming && streamingToolCalls.length > 0 && (
                    <div className="ml-2 space-y-1.5">
                      {/* Streaming thinking tools get ThinkingCard with mutual exclusion */}
                      {streamingToolCalls
                        .filter((tool) => isThinkingTool(tool.isReasoning))
                        .map((tool, idx) => (
                          <ThinkingCard
                            key={`streaming-thinking-${idx}`}
                            tool={tool}
                            isStreaming={true}
                            isExpanded={expandedThinkingCardId === tool.id}
                            onToggle={handleThinkingCardToggle}
                          />
                        ))}
                      {/* Streaming regular tools get standard badge */}
                      {streamingToolCalls
                        .filter((tool) => !isThinkingTool(tool.isReasoning))
                        .map((tool, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded"
                          >
                            <Wrench className="w-3.5 h-3.5 text-amber-600" />
                            <span className="font-medium">{tool.name}</span>
                            {tool.status === 'running' && (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin text-amber-600" />
                                {tool.progress && (
                                  <span className="text-amber-700 truncate max-w-[200px]">{tool.progress}</span>
                                )}
                              </>
                            )}
                            {tool.status === 'completed' && tool.executionTime && (
                              <span className="text-emerald-600 font-medium">
                                {tool.executionTime.toFixed(2)}s
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Streaming Status Bar */}
                {isStreaming && (
                  <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-blue-700">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Generating...</span>
                    </div>
                    <div className="flex items-center gap-3 text-blue-600">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {elapsedTime.toFixed(1)}s
                      </span>
                    </div>
                  </div>
                )}

                {/* Voice Recording Indicator */}
                {voiceRecording.isRecording && (
                  <div className="px-4 py-2 bg-red-50 border-t border-red-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-red-700">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                      <Mic className="w-4 h-4" />
                      <span className="text-sm font-medium">Recording...</span>
                    </div>
                    <div className="flex items-center gap-3 text-red-600 text-sm">
                      <span className="font-mono w-8 text-right">{Math.max(0, 30 - Math.floor(voiceRecording.duration))}s</span>
                      <span className="text-xs text-red-500">Release Space to send</span>
                    </div>
                  </div>
                )}

                {/* Voice Processing Indicator */}
                {voiceRecording.isProcessing && (
                  <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 flex items-center gap-2 text-amber-700">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Processing voice...</span>
                  </div>
                )}

                {/* Input */}
                <div className="p-3 border-t border-slate-200">
                  {/* Hidden audio file input */}
                  <input
                    ref={audioFileInputRef}
                    type="file"
                    accept="audio/*,.wav,.mp3,.m4a,.webm,.ogg"
                    onChange={handleAudioFileSelect}
                    className="hidden"
                  />
                  {/* Hidden image file input */}
                  {isMultimodalEnabled && (
                    <input
                      ref={imageFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageFileSelect}
                      className="hidden"
                    />
                  )}

                  {/* Image Preview Row */}
                  {uploadedImages.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {uploadedImages.map((img) => (
                        <div key={img.id} className="relative group">
                          <img
                            src={img.preview}
                            alt={img.filename}
                            className="w-16 h-16 object-cover rounded border border-slate-200"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(img.id)}
                            className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {isUploadingImages && (
                        <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded border border-slate-200">
                          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={handleNewChat}
                      className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                      title="New chat"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => audioFileInputRef.current?.click()}
                      disabled={isStreaming || !selectedAgent || voiceRecording.isRecording}
                      className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Upload audio file"
                    >
                      <Mic className="w-4 h-4" />
                    </button>
                    {/* Image upload button - only show if multimodal is enabled */}
                    {isMultimodalEnabled && (
                      <button
                        onClick={() => imageFileInputRef.current?.click()}
                        disabled={isStreaming || !selectedAgent || voiceRecording.isRecording || uploadedImages.length >= 4}
                        className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        title={uploadedImages.length >= 4 ? "Max 4 images" : "Upload image"}
                      >
                        <ImageIcon className="w-4 h-4" />
                      </button>
                    )}
                    <Input
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      onFocus={() => setInputFocused(true)}
                      onBlur={() => setInputFocused(false)}
                      placeholder={voiceRecording.isRecording ? "Recording..." : "Type or hold Space to speak..."}
                      disabled={isStreaming || !selectedAgent || voiceRecording.isRecording}
                      className="flex-1 text-sm"
                    />
                    <Button
                      variant="primary"
                      onClick={handleSend}
                      disabled={(!inputValue.trim() && uploadedImages.length === 0) || isStreaming || !selectedAgent || voiceRecording.isRecording}
                      className="px-3"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Voice hint */}
                  {!isStreaming && !voiceRecording.isRecording && !inputFocused && (
                    <div className="mt-1.5 text-xs text-slate-400 flex items-center gap-1">
                      <Mic className="w-3 h-3" />
                      <span>Hold <kbd className="px-1 py-0.5 bg-slate-100 rounded text-slate-500 font-mono">Space</kbd> or click mic to upload</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Topics Tab (Chat History) */}
            {activeTab === 'topics' && (
              <div className="flex-1 overflow-y-auto">
                <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-sm text-slate-600">
                    {sortedTopics.length} conversation{sortedTopics.length !== 1 ? 's' : ''}
                  </span>
                  <Button variant="secondary" size="sm" onClick={handleNewChat}>
                    <Plus className="w-4 h-4 mr-1" />
                    New
                  </Button>
                </div>

                <div className="p-3 space-y-2">
                  {sortedTopics.length > 0 ? (
                    sortedTopics.map((topic) => (
                      <div
                        key={topic.topic_id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          topicId === topic.topic_id
                            ? 'border-blue-300 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                        onClick={() => handleLoadTopic(topic)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          {editingTopicId === topic.topic_id ? (
                            <div className="flex-1 flex items-center gap-2">
                              <Input
                                value={editingTopicName}
                                onChange={(e) => setEditingTopicName(e.target.value)}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') {
                                    handleRenameTopic(topic, editingTopicName);
                                  } else if (e.key === 'Escape') {
                                    setEditingTopicId(null);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm h-7"
                                autoFocus
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRenameTopic(topic, editingTopicName);
                                }}
                                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-slate-800 truncate">
                                {topic.title || `Chat ${topic.topic_id.slice(0, 8)}`}
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                <span>{topic.message_count} messages</span>
                                <span className="text-slate-300">•</span>
                                <span>{formatRelativeTime(topic.updated_at || topic.created_at)}</span>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTopicId(topic.topic_id);
                                setEditingTopicName(topic.title || '');
                              }}
                              className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                              title="Rename"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenPromoteDialog(topic);
                              }}
                              className="p-1 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                              title="Promote to Test Scenario"
                            >
                              <TrendingUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTopic(topic);
                              }}
                              className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-slate-400 py-8">
                      <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No chat history</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Memory Tab */}
            {activeTab === 'memory' && (
              <div className="flex-1 overflow-y-auto flex flex-col">
                <div className="p-3 border-b border-slate-100 space-y-2">
                  {/* Scope and Category Filters */}
                  <div className="flex flex-wrap gap-2 items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-500">Scope:</span>
                      {(['all', 'user', 'agent'] as const).map((scope) => (
                        <button
                          key={scope}
                          onClick={() => setMemoryScope(scope)}
                          className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                            memoryScope === scope
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {scope === 'all' ? 'All' : scope === 'user' ? 'User' : 'Agent'}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-500">Type:</span>
                      {(['all', 'permanent', 'temporal'] as const).map((type) => (
                        <button
                          key={type}
                          onClick={() => setCategoryType(type)}
                          className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                            categoryType === type
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {type === 'all' ? 'All' : type === 'permanent' ? 'Facts' : 'Temporal'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={memorySearch}
                      onChange={(e) => setMemorySearch(e.target.value)}
                      placeholder="Search memories..."
                      className="pl-9 text-sm"
                    />
                  </div>

                  {/* Active Filters */}
                  {Object.keys(activeMemoryFilters).length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Filter className="w-3.5 h-3.5 text-slate-400" />
                      {Object.entries(activeMemoryFilters).map(([field, value]) => (
                        <button
                          key={field}
                          onClick={() => toggleMemoryFilter(field, value)}
                          className={`px-2 py-0.5 text-xs rounded-full ${getMetadataBadgeColor(field)} flex items-center gap-1`}
                        >
                          {field}: {String(value).slice(0, 15)}
                          <X className="w-3 h-3" />
                        </button>
                      ))}
                      <button
                        onClick={clearMemoryFilters}
                        className="text-xs text-slate-500 hover:text-slate-700"
                      >
                        Clear all
                      </button>
                    </div>
                  )}

                  <div className="text-xs text-slate-500">
                    {filteredMemories.length} of {sortedMemories.length} memories
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {loadingMemories ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : filteredMemories.length > 0 ? (
                    filteredMemories.map((memory) => (
                      <div
                        key={memory.id}
                        className="p-3 rounded-lg border border-slate-200 hover:border-slate-300"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <button
                            onClick={() => {
                              setExpandedMemories((prev) => {
                                const next = new Set(prev);
                                next.has(memory.id) ? next.delete(memory.id) : next.add(memory.id);
                                return next;
                              });
                            }}
                            className="text-slate-400 hover:text-slate-600 mt-0.5"
                          >
                            {expandedMemories.has(memory.id) ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm text-slate-700 ${expandedMemories.has(memory.id) ? '' : 'line-clamp-2'}`}>
                              {memory.memory}
                            </p>

                            {/* Topics Tags */}
                            {memory.topics && memory.topics.length > 0 && (
                              <div className="flex items-center gap-1 mt-2 flex-wrap">
                                <Tag className="w-3 h-3 text-slate-400" />
                                {memory.topics.map((topic, idx) => (
                                  <span key={idx} className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                                    {topic}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Metadata Badges */}
                            {memory.metadata && (
                              <div className="flex items-center gap-1 mt-2 flex-wrap">
                                {['agent_type', 'source', 'type', 'date'].map((field) =>
                                  memory.metadata?.[field] ? (
                                    <button
                                      key={field}
                                      onClick={() => toggleMemoryFilter(field, memory.metadata![field])}
                                      className={`px-1.5 py-0.5 text-xs rounded cursor-pointer ${getMetadataBadgeColor(field)} ${
                                        activeMemoryFilters[field] === memory.metadata![field] ? 'ring-2 ring-offset-1' : ''
                                      }`}
                                    >
                                      {String(memory.metadata![field]).slice(0, 20)}
                                    </button>
                                  ) : null
                                )}
                              </div>
                            )}

                            {/* Timestamp */}
                            <div className="text-xs text-slate-400 mt-2">
                              {formatRelativeTime(memory.last_updated || memory.updated_at || memory.created_at || '')}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteMemory(memory.id)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Expanded Metadata */}
                        {expandedMemories.has(memory.id) && memory.metadata && (
                          <div className="mt-3 ml-6 p-2 bg-slate-50 rounded text-xs font-mono text-slate-600 overflow-x-auto">
                            <pre>{JSON.stringify(memory.metadata, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-slate-400 py-8">
                      <Brain className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No memories found</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Birth Charts Tab */}
            {activeTab === 'birthcharts' && (
              <div className="flex-1 overflow-y-auto flex flex-col">
                <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-sm text-slate-600">
                    {sortedBirthCharts.length} chart{sortedBirthCharts.length !== 1 ? 's' : ''}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowBirthChartForm(true);
                      setEditingChartId(null);
                      setChartForm({ name: '', birth_date: '', birth_time: '', birth_place: '', gender: '', relation: '', notes: '' });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Chart
                  </Button>
                </div>

                {showBirthChartForm && (
                  <div className="p-3 border-b border-slate-100 bg-slate-50 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={chartForm.name}
                        onChange={(e) => setChartForm({ ...chartForm, name: e.target.value })}
                        placeholder="Name"
                        className="text-sm"
                      />
                      <Input
                        type="date"
                        value={chartForm.birth_date}
                        onChange={(e) => setChartForm({ ...chartForm, birth_date: e.target.value })}
                        className="text-sm"
                      />
                      <Input
                        type="time"
                        value={chartForm.birth_time}
                        onChange={(e) => setChartForm({ ...chartForm, birth_time: e.target.value })}
                        className="text-sm"
                      />
                      <Input
                        value={chartForm.birth_place}
                        onChange={(e) => setChartForm({ ...chartForm, birth_place: e.target.value })}
                        placeholder="Birth place"
                        className="text-sm"
                      />
                      <select
                        value={chartForm.gender}
                        onChange={(e) => setChartForm({ ...chartForm, gender: e.target.value })}
                        className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white"
                      >
                        <option value="">Gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      <select
                        value={chartForm.relation}
                        onChange={(e) => setChartForm({ ...chartForm, relation: e.target.value })}
                        className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white"
                      >
                        <option value="">Relation</option>
                        <option value="self">Self</option>
                        <option value="spouse">Spouse</option>
                        <option value="child">Child</option>
                        <option value="parent">Parent</option>
                        <option value="friend">Friend</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <Input
                      value={chartForm.notes}
                      onChange={(e) => setChartForm({ ...chartForm, notes: e.target.value })}
                      placeholder="Notes (optional)"
                      className="text-sm"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setShowBirthChartForm(false);
                          setEditingChartId(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSaveBirthChart}
                        disabled={!chartForm.name || !chartForm.birth_date}
                      >
                        {editingChartId ? 'Update' : 'Create'}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {loadingBirthCharts ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : sortedBirthCharts.length > 0 ? (
                    sortedBirthCharts.map((chart) => {
                      const birthInfo = getChartBirthInfo(chart);
                      const displayName = getChartDisplayName(chart);

                      return (
                        <div
                          key={chart.id}
                          className={`p-3 rounded-lg border ${
                            chart.is_self
                              ? 'border-purple-300 bg-purple-50'
                              : chart.is_primary
                              ? 'border-amber-300 bg-amber-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm text-slate-800">{displayName}</span>
                                {chart.is_self && (
                                  <span className="px-1.5 py-0.5 text-xs bg-purple-200 text-purple-800 rounded">
                                    Self
                                  </span>
                                )}
                                {chart.is_primary && !chart.is_self && (
                                  <span className="px-1.5 py-0.5 text-xs bg-amber-200 text-amber-800 rounded">
                                    Primary
                                  </span>
                                )}
                                {chart.relation && chart.relation !== 'self' && (
                                  <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                    {chart.relation}
                                  </span>
                                )}
                              </div>

                              <div className="mt-2 space-y-1 text-xs text-slate-600">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                  <span>
                                    {birthInfo.date}
                                    {birthInfo.time && ` at ${birthInfo.time}`}
                                  </span>
                                </div>

                                {birthInfo.location && (
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                                    <span>{birthInfo.location}</span>
                                  </div>
                                )}

                                <div className="flex items-center gap-2 flex-wrap">
                                  {birthInfo.gender && (
                                    <span className="flex items-center gap-1">
                                      <User className="w-3 h-3 text-slate-400" />
                                      {birthInfo.gender === 'M' ? 'Male' : birthInfo.gender === 'F' ? 'Female' : birthInfo.gender}
                                    </span>
                                  )}
                                  {birthInfo.timezone && (
                                    <span className="text-slate-400">{birthInfo.timezone}</span>
                                  )}
                                  {birthInfo.birthType && (
                                    <span className={`px-1 py-0.5 rounded text-xs ${
                                      birthInfo.birthType === 'day' ? 'bg-yellow-100 text-yellow-700' : 'bg-indigo-100 text-indigo-700'
                                    }`}>
                                      {birthInfo.birthType === 'day' ? '日生' : '夜生'}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {chart.notes && (
                                <div className="mt-2 text-xs text-slate-500 italic">
                                  {chart.notes}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-1">
                              {!chart.is_primary && (
                                <button
                                  onClick={() => handleSetPrimaryChart(chart.id)}
                                  className="p-1 text-slate-400 hover:text-amber-500"
                                  title="Set as primary"
                                >
                                  <Star className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => handleEditBirthChart(chart)}
                                className="p-1 text-slate-400 hover:text-slate-600"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteBirthChart(chart.id, displayName)}
                                className="p-1 text-slate-400 hover:text-red-500"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center text-slate-400 py-8">
                      <Star className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No birth charts</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>

            {/* Debug Side Panel - Agent Debugging */}
            {showDebugPanel && (
              <div
                className="overflow-y-auto bg-slate-900 text-slate-100 p-3 space-y-3 text-xs font-mono shrink-0"
                style={{ width: DEBUG_PANEL_WIDTH }}
              >
                {/* Clear Button */}
                <div className="flex justify-end">
                  <button
                    onClick={clearDebugEvents}
                    className="text-xs text-slate-400 hover:text-slate-200 px-2 py-1 hover:bg-slate-700 rounded"
                  >
                    Clear Events
                  </button>
                </div>

                {/* Session Info */}
                <div className="bg-slate-800/50 rounded-lg p-2">
                  <div className="text-cyan-400 mb-1.5 font-semibold flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-cyan-400"></span>
                    Session
                  </div>
                  <div className="space-y-1 text-slate-300">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Agent:</span>
                      <span className="text-cyan-300">{selectedAgent || 'none'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Session ID:</span>
                      <span className="text-purple-300 truncate max-w-[140px]" title={topicId || undefined}>
                        {topicId ? topicId.slice(0, 12) + '...' : 'null'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Messages:</span>
                      <span>{messages.length}</span>
                    </div>
                  </div>
                </div>

                {/* Streaming Status */}
                <div className={`rounded-lg p-2 ${isStreaming ? 'bg-amber-900/30 border border-amber-700/50' : 'bg-slate-800/50'}`}>
                  <div className={`mb-1.5 font-semibold flex items-center gap-1 ${isStreaming ? 'text-amber-400' : 'text-slate-500'}`}>
                    <span className={`w-2 h-2 rounded-full ${isStreaming ? 'bg-amber-400 animate-pulse' : 'bg-slate-600'}`}></span>
                    Streaming
                  </div>
                  <div className="space-y-1 text-slate-300">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Status:</span>
                      <span className={isStreaming ? 'text-amber-300' : 'text-slate-500'}>
                        {isStreaming ? 'ACTIVE' : 'idle'}
                      </span>
                    </div>
                    {isStreaming && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Elapsed:</span>
                          <span className="text-amber-300">{elapsedTime.toFixed(2)}s</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Tools Running:</span>
                          <span className="text-amber-300">{streamingToolCalls.filter(t => t.status === 'running').length}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Active Tool Calls */}
                {streamingToolCalls.length > 0 && (
                  <div className="bg-slate-800/50 rounded-lg p-2">
                    <div className="text-orange-400 mb-1.5 font-semibold flex items-center gap-1">
                      <Wrench className="w-3 h-3" />
                      Tool Calls ({streamingToolCalls.length})
                    </div>
                    <div className="space-y-1.5">
                      {streamingToolCalls.map((tool, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded px-2 py-1">
                          <span className="truncate max-w-[120px] text-slate-300" title={tool.name}>
                            {tool.name}
                          </span>
                          <div className="flex items-center gap-1">
                            {tool.status === 'running' ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin text-amber-400" />
                                <span className="text-amber-400">running</span>
                              </>
                            ) : (
                              <>
                                <Check className="w-3 h-3 text-green-400" />
                                <span className="text-green-400">
                                  {tool.executionTime ? `${tool.executionTime.toFixed(2)}s` : 'done'}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Session Metrics */}
                <div className="bg-slate-800/50 rounded-lg p-2">
                  <div className="text-emerald-400 mb-1.5 font-semibold flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Session Metrics
                  </div>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-slate-300">
                    <div className="text-slate-500">Requests:</div>
                    <div className="text-right">{sessionMetrics.totalRequests}</div>
                    <div className="text-slate-500">Input Tokens:</div>
                    <div className="text-right text-blue-300">{sessionMetrics.totalInputTokens.toLocaleString()}</div>
                    <div className="text-slate-500">Output Tokens:</div>
                    <div className="text-right text-green-300">{sessionMetrics.totalOutputTokens.toLocaleString()}</div>
                    <div className="text-slate-500">Total Tokens:</div>
                    <div className="text-right text-amber-300">
                      {(sessionMetrics.totalInputTokens + sessionMetrics.totalOutputTokens).toLocaleString()}
                    </div>
                    <div className="text-slate-500">Avg Response:</div>
                    <div className="text-right">
                      {sessionMetrics.avgResponseTime > 0 ? `${sessionMetrics.avgResponseTime.toFixed(2)}s` : '-'}
                    </div>
                  </div>
                </div>

                {/* Current Request/Response */}
                {lastRequest && (
                  <div className="bg-slate-800/50 rounded-lg p-2">
                    <div className="text-blue-400 mb-1.5 font-semibold">Last Request</div>
                    <div className="text-slate-400 text-[10px] bg-slate-900/50 rounded p-1.5 overflow-x-auto max-h-20 overflow-y-auto">
                      <pre>{JSON.stringify(lastRequest, null, 1)}</pre>
                    </div>
                  </div>
                )}

                {lastResponse && (
                  <div className="bg-slate-800/50 rounded-lg p-2">
                    <div className="text-purple-400 mb-1.5 font-semibold">Last Response</div>
                    <div className="text-slate-400 text-[10px] bg-slate-900/50 rounded p-1.5 overflow-x-auto max-h-20 overflow-y-auto">
                      <pre>{JSON.stringify(lastResponse, null, 1)}</pre>
                    </div>
                  </div>
                )}

                {/* Event Log */}
                <div className="bg-slate-800/50 rounded-lg p-2">
                  <div className="text-slate-400 mb-1.5 font-semibold flex items-center gap-1">
                    <History className="w-3 h-3" />
                    Event Log ({debugEvents.length})
                  </div>
                  <div className="space-y-0.5 max-h-48 overflow-y-auto">
                    {debugEvents.length === 0 ? (
                      <div className="text-slate-600 text-center py-2">No events yet</div>
                    ) : (
                      [...debugEvents].reverse().map((event, idx) => (
                        <div
                          key={idx}
                          className={`flex items-start gap-1.5 text-[10px] py-0.5 px-1 rounded ${
                            event.type === 'error' ? 'bg-red-900/30 text-red-300' :
                            event.type === 'request' ? 'bg-blue-900/30 text-blue-300' :
                            event.type === 'done' ? 'bg-green-900/30 text-green-300' :
                            event.type === 'tool_start' || event.type === 'tool_end' ? 'bg-orange-900/30 text-orange-300' :
                            event.type === 'metrics' ? 'bg-purple-900/30 text-purple-300' :
                            'bg-slate-900/30 text-slate-400'
                          }`}
                        >
                          <span className="text-slate-600 shrink-0">{event.time}</span>
                          <span className={`font-medium shrink-0 ${
                            event.type === 'error' ? 'text-red-400' :
                            event.type === 'request' ? 'text-blue-400' :
                            event.type === 'done' ? 'text-green-400' :
                            event.type === 'tool_start' || event.type === 'tool_end' ? 'text-orange-400' :
                            event.type === 'metrics' ? 'text-purple-400' :
                            'text-slate-500'
                          }`}>
                            {event.type}
                          </span>
                          <span className="truncate text-slate-500" title={JSON.stringify(event.data)}>
                            {typeof event.data === 'object'
                              ? Object.entries(event.data).slice(0, 2).map(([k, v]) => `${k}:${String(v).slice(0, 10)}`).join(' ')
                              : String(event.data).slice(0, 30)
                            }
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Panel Size Info */}
                <div className="text-slate-600 text-[10px] border-t border-slate-700 pt-2">
                  Panel: {size.width}x{size.height}px
                </div>
              </div>
            )}
          </div>
        </>
      )}
      </div>

      {/* Promote Chat Dialog */}
      <PromoteChatDialog
        isOpen={!!promoteTopicId}
        onClose={() => {
          setPromoteTopicId(null);
          setPromoteTopicTitle('');
        }}
        onSubmit={handlePromoteChat}
        isSubmitting={promoteMutation.isPending}
        categories={evalCategories}
        initialTopicId={promoteTopicId || undefined}
        initialTopicTitle={promoteTopicTitle || undefined}
        initialAgentType={selectedAgent || undefined}
      />
    </div>
  );
};

export default ChatPanel;
